if (!customElements.get('complete-look')) {
  customElements.define('complete-look', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.querySelector('scroll-carousel')?.addEventListener('carousel:change', (event) => {
        const counter = this.querySelector('[data-look-page]');
        if (counter) counter.textContent = String(event.detail.index + 1);
      }, {signal: this.controller.signal});
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

if (!customElements.get('complete-look-placement')) {
  customElements.define('complete-look-placement', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();

      this.trigger = this.trigger || this.querySelector('[data-look-trigger]');
      this.drawer = this.drawer || this.querySelector('[data-look-drawer]');
      this.desktopMount = this.querySelector('[data-look-desktop-mount]');
      this.look = this.look || this.desktopMount?.querySelector('complete-look');
      const imageList = this.closest('.product')?.querySelector('.product-gallery__image-list');

      if (!this.trigger || !this.drawer || !this.desktopMount || !this.look || !imageList) return;

      imageList.append(this.trigger);
      const mobile = window.matchMedia('(max-width: 999px)');
      const syncLayout = () => {
        if (!mobile.matches && this.drawer.hasAttribute('open')) this.drawer.hide();
      };

      mobile.addEventListener('change', syncLayout, {signal: this.controller.signal});
      this.drawer.addEventListener('dialog:before-show', () => {
        this.drawer.append(this.look);
      }, {signal: this.controller.signal});
      this.drawer.addEventListener('dialog:after-hide', () => {
        this.desktopMount.append(this.look);
      }, {signal: this.controller.signal});
      document.addEventListener('variant:add', () => {
        if (this.drawer.hasAttribute('open')) this.drawer.hide();
      }, {signal: this.controller.signal});

      syncLayout();
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.trigger) this.prepend(this.trigger);
      if (this.look && this.desktopMount) this.desktopMount.append(this.look);
      if (this.drawer?.parentElement === document.body) {
        this.drawer.removeAttribute('open');
        this.drawer.remove();
        this.append(this.drawer);
      }
    }
  });
}

// Only outfit quick buy requires an explicit size choice. Native variant rendering
// still resolves availability, prices and the selected color.
if (!window.completeLookSizeChoiceInstalled) {
  window.completeLookSizeChoiceInstalled = true;
  document.addEventListener('dialog:before-show', (event) => {
    const modal = event.target;
    if (!modal.matches('quick-buy-modal[data-look-size-choice]')) return;
    const selector = '.variant-picker__option--size:not([data-bundle-size-target]) input[data-option-position]';
    if (!modal.querySelector(selector)) return;
    const controller = new AbortController();
    const selected = new Set();
    const automatic = new Set();
    const availableOptions = JSON.parse(modal.dataset.lookAvailableOptions || '[]');
    const fallback = new Map();
    const buttons = new Map();
    let resolving = false;
    const inputs = () => [...modal.querySelectorAll(selector)];
    const pending = () => inputs().some(input => !selected.has(input.name));
    const refresh = () => {
      observer.disconnect();
      if (!resolving) {
        const sizeInputs = inputs();
        const otherOptions = [...modal.querySelectorAll('input[data-option-position]:checked')]
          .filter(input => !sizeInputs.includes(input));
        const combinations = availableOptions.filter(options => otherOptions.every(input =>
          options[Number(input.dataset.optionPosition) - 1] === input.dataset.optionValueName));
        const groups = new Map();
        for (const input of sizeInputs) {
          if (!groups.has(input.name)) groups.set(input.name, []);
          groups.get(input.name).push(input);
        }
        for (const [name, group] of groups) {
          const available = group.filter(input => !input.disabled && combinations.some(options =>
            options[Number(input.dataset.optionPosition) - 1] === input.dataset.optionValueName));
          if (available.length === 1) {
            selected.add(name);
            automatic.add(name);
            if (!available[0].checked) {
              const onlySize = available[0];
              resolving = true;
              queueMicrotask(() => {
                if (!onlySize.isConnected || controller.signal.aborted) return;
                onlySize.checked = true;
                onlySize.dispatchEvent(new Event('change', {bubbles: true}));
              });
            }
          }
        }
      }
      for (const input of inputs()) {
        if (!selected.has(input.name)) {
          if (input.checked) fallback.set(input.name, input.value);
          input.checked = false;
          const group = input.closest('fieldset');
          const label = group.querySelector('.select > span, .variant-picker__option-info legend + span');
          if (label) label.textContent = 'Seleziona una taglia';
        }
      }
      const locked = pending() || resolving;
      modal.querySelectorAll('buy-buttons button[type="submit"]').forEach(button => {
        if (locked) {
          if (!buttons.has(button)) buttons.set(button, {html: button.innerHTML, disabled: button.disabled});
          button.disabled = true;
          button.textContent = 'Seleziona una taglia';
        } else if (buttons.has(button)) {
          const original = buttons.get(button);
          button.innerHTML = original.html;
          button.disabled = original.disabled;
          buttons.delete(button);
        }
      });
      modal.querySelectorAll('.shopify-payment-button').forEach(button => {
        button.hidden = locked;
      });
      observer.observe(modal, {childList: true, subtree: true});
    };
    const observer = new MutationObserver(refresh);
    const beginVariantSelection = (input, form) => {
      if (input?.matches(selector)) {
        selected.add(input.name);
      } else {
        // A size selected automatically for one color must not become an
        // implicit choice when another color offers several sizes.
        for (const name of automatic) selected.delete(name);
        automatic.clear();
      }
      resolving = true;
      // The quick-buy rerender can detach the old form before it emits this event.
      form?.addEventListener('variant:change', () => {
        resolving = false;
        refresh();
      }, {once: true, signal: controller.signal});
      // Native Prestige expects a complete combination while changing color.
      // Restore technical values only for this synchronous event dispatch;
      // they never count as a customer's size choice.
      for (const input of inputs()) {
        if (!selected.has(input.name) && fallback.get(input.name) === input.value) input.checked = true;
      }
      queueMicrotask(refresh);
    };
    modal.addEventListener('change', event => {
      if (event.target.matches('input[data-option-position]')) beginVariantSelection(event.target, event.target.form);
    }, {capture: true, signal: controller.signal});
    modal.addEventListener('gallery:variant-select', event => {
      beginVariantSelection(null, event.detail.form);
    }, {signal: controller.signal});
    modal.addEventListener('variant:change', () => {
      resolving = false;
      refresh();
    }, {signal: controller.signal});
    modal.addEventListener('submit', event => {
      if (pending() || resolving) {
        event.preventDefault();
        event.stopImmediatePropagation();
        inputs().find(input => !input.disabled)?.focus();
      }
    }, {capture: true, signal: controller.signal});
    modal.addEventListener('dialog:after-hide', () => {
      observer.disconnect();
      controller.abort();
    }, {once: true, signal: controller.signal});
    refresh();
  }, true);
}
